import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import type {
  NotificationCatalogEvent,
  NotificationFrequency,
} from '@ewentscompany/ewents-settings';
import { buildEndpointUrl, SERVICES } from '../shared/constants/api.constants';

export type NotificationChannel = 'EMAIL' | 'SLACK' | 'PUSH' | 'WHATSAPP';

export interface NotificationChannelConfig {
  id: string;
  channel: NotificationChannel;
  enabled: boolean;
  config: Record<string, unknown>;
}

export interface NotificationRule {
  id?: string;
  domain: string;
  event: string;
  enabled: boolean;
  channels: NotificationChannel[];
  recipientUserIds: string[];
  excludedUserIds?: string[];
  offsetsDays: number[];
  frequency?: NotificationFrequency | null;
}

export interface HoursMissingAudienceRow {
  userId: string;
  talentId: number;
  email: string;
  firstName: string | null;
  lastName: string | null;
  avatarUrl: string | null;
}

@Injectable({ providedIn: 'root' })
export class NotificationHubService {
  private readonly http = inject(HttpClient);
  private readonly base = SERVICES.NOTIFICATION_HUB;

  getCatalog(): Observable<NotificationCatalogEvent[]> {
    return this.http.get<NotificationCatalogEvent[]>(buildEndpointUrl(this.base, '/catalog'));
  }

  getChannels(): Observable<NotificationChannelConfig[]> {
    return this.http.get<NotificationChannelConfig[]>(buildEndpointUrl(this.base, '/channels'));
  }

  patchChannels(
    channels: Array<{
      channel: NotificationChannel;
      enabled?: boolean;
      config?: Record<string, unknown>;
    }>
  ): Observable<NotificationChannelConfig[]> {
    return this.http.patch<NotificationChannelConfig[]>(buildEndpointUrl(this.base, '/channels'), {
      channels,
    });
  }

  getRules(domain?: string): Observable<NotificationRule[]> {
    const q = domain ? `?domain=${encodeURIComponent(domain)}` : '';
    return this.http.get<NotificationRule[]>(buildEndpointUrl(this.base, `/rules${q}`));
  }

  putRules(rules: NotificationRule[]): Observable<NotificationRule[]> {
    return this.http.put<NotificationRule[]>(buildEndpointUrl(this.base, '/rules'), { rules });
  }

  getHoursMissingAudience(): Observable<HoursMissingAudienceRow[]> {
    return this.http.get<HoursMissingAudienceRow[]>(
      buildEndpointUrl(this.base, '/audience/timesheets/hours_missing')
    );
  }

  getPushStatus(): Observable<{ enabled: boolean }> {
    return this.http.get<{ enabled: boolean }>(buildEndpointUrl(this.base, '/push/status'));
  }

  getDeliveryStatus(): Observable<{ fcm: boolean; slack: boolean; whatsapp?: boolean }> {
    return this.http.get<{ fcm: boolean; slack: boolean; whatsapp?: boolean }>(
      buildEndpointUrl(this.base, '/delivery-status')
    );
  }

  subscribePush(body: {
    token: string;
    platform: 'web' | 'ios' | 'android';
    userAgent?: string;
  }): Observable<unknown> {
    return this.http.post(buildEndpointUrl(this.base, '/push/subscribe'), body);
  }

  unsubscribePush(body: { token: string }): Observable<{ ok: true }> {
    return this.http.post<{ ok: true }>(buildEndpointUrl(this.base, '/push/unsubscribe'), body);
  }

  getInbox(params?: { limit?: number; unreadOnly?: boolean }): Observable<UserInboxNotification[]> {
    const q = new URLSearchParams();
    if (params?.limit != null) q.set('limit', String(params.limit));
    if (params?.unreadOnly) q.set('unreadOnly', 'true');
    const qs = q.toString();
    return this.http.get<UserInboxNotification[]>(
      buildEndpointUrl(this.base, `/inbox${qs ? `?${qs}` : ''}`)
    );
  }

  getInboxUnreadCount(): Observable<{ count: number }> {
    return this.http.get<{ count: number }>(buildEndpointUrl(this.base, '/inbox/unread-count'));
  }

  markInboxRead(id: string): Observable<InboxMarkReadDelta> {
    return this.http.post<InboxMarkReadDelta>(buildEndpointUrl(this.base, `/inbox/${id}/read`), {});
  }

  markInboxAllRead(): Observable<{ updated: number }> {
    return this.http.post<{ updated: number }>(buildEndpointUrl(this.base, '/inbox/read-all'), {});
  }

  deleteInbox(id: string): Observable<{ id: string; deleted: true }> {
    return this.http.delete<{ id: string; deleted: true }>(
      buildEndpointUrl(this.base, `/inbox/${id}`)
    );
  }
}

export interface UserInboxNotification {
  id: string;
  title: string;
  body: string;
  link: string | null;
  entityType: string | null;
  entityId: string | null;
  readAt: string | null;
  createdAt: string;
}

export interface InboxMarkReadDelta {
  id: string;
  readAt: string;
}
